import { Routes, Route } from "react-router-dom"
import { Toaster, ToastBar, toast } from "react-hot-toast"

import { ROUTES } from "./constants"
import { useScrollToTop } from "./hooks"

// Layout
import { Main } from "./pages/Main/Main"
import { ModalBase } from "./components/modals/ModalBase/ModalBase"

// Public pages
import { Home } from "./pages/Home/Home"
import { NotFound } from "./pages/NotFound/NotFound"

// Protected pages
import { Profile } from "./pages/Profile/Profile"
import { ProtectedRoute } from "./components/features/ProtectedRoute/ProtectedRoute"
import { AdminRoute } from "./components/features/AdminRoute/AdminRoute"

// Admin pages
import { AdminLayout } from "./pages/admin/AdminLayout/AdminLayout"
import { Dashboard } from "./pages/admin/Dashboard/Dashboard"
import { AdminOrders } from "./pages/admin/AdminOrders/AdminOrders"
import { AdminMaterials } from "./pages/admin/AdminMaterials/AdminMaterials"
import { AdminSizes } from "./pages/admin/AdminSizes/AdminSizes"
import { AdminPrices } from "./pages/admin/AdminPrices/AdminPrices"
import { AdminShipping } from "./pages/admin/AdminShipping/AdminShipping"

// Auth pages
import { Login } from "./pages/auth/Login/Login"
import { Register } from "./pages/auth/Register/Register"
import { VerifyEmail } from "./pages/auth/VerifyEmail/VerifyEmail"
import { ForgotPassword } from "./pages/auth/ForgotPassword/ForgotPassword"
import { ChangePassword } from "./pages/auth/ChangePassword/ChangePassword"

// Editor & Projects pages
import { Editor } from "./pages/Editor/Editor"
import { ProjectsList } from "./pages/Projects/ProjectsList/ProjectsList"
import { NewProject } from "./pages/Projects/NewProject/NewProject"

// Order pages
import { OrdersList } from "./pages/Orders/OrdersList/OrdersList"
import { OrderDetail } from "./pages/Orders/OrderDetail/OrderDetail"
import { OrderCheckout } from "./pages/Orders/OrderCheckout/OrderCheckout"
import { Cart } from "./pages/Cart/Cart"
import { Checkout } from "./pages/Checkout/Checkout"

// Legal pages
import { PrivacyPolicy } from "./pages/Legal/PrivacyPolicy/PrivacyPolicy"
import { TermsOfService } from "./pages/Legal/TermsOfService/TermsOfService"

// Canva callback
import { CanvaCallback } from "./pages/CanvaCallback/CanvaCallback"

import './App.css'

function App() {
    // The new AuthStore no longer needs initialization as it
    // checks token validity on creation
    useScrollToTop();

    return (
        <div className="App">
            <ModalBase />
            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 5000,
                    success: { className: "toast-success" },
                    error: { className: "toast-error" },
                    style: {
                        zIndex: 20
                    }
                }}
                children={(toasts) => (
                    <ToastBar toast={toasts}>
                        {({ icon, message }) => (
                            <div
                              onClick={() => toast.dismiss()}
                              style={{ cursor: "pointer", display: "flex"}}
                            >
                                {icon}
                                {message}
                            </div>
                        )}
                    </ToastBar>
                )}
            />
            <Routes>
                {/* Public auth endpoints */}
                <Route path={ROUTES.login} element={<Login />} />
                <Route path={ROUTES.register} element={<Register />} />
                <Route path={ROUTES.verifyEmail} element={<VerifyEmail />} />
                <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />
                
                {/* Protected auth endpoints */}
                <Route element={<ProtectedRoute redirectToLogin />}>
                    <Route path={ROUTES.changePassword} element={<ChangePassword />} />
                </Route>

                {/* Canva OAuth callback */}
                <Route element={<ProtectedRoute redirectToLogin />}>
                    <Route path={ROUTES.canvaCallback} element={<CanvaCallback />} />
                </Route>

                {/* Editor & Order Checkout - full-screen, no Main layout */}
                <Route element={<ProtectedRoute redirectToLogin />}>
                    <Route path={ROUTES.editor} element={<Editor />} />
                    <Route path={ROUTES.orderCheckout} element={<OrderCheckout />} />
                </Route>

                {/* Admin routes */}
                <Route element={<AdminRoute />}>
                    <Route path={ROUTES.admin} element={<AdminLayout />}>
                        <Route path={ROUTES.adminDashboard} element={<Dashboard />} />
                        <Route path={ROUTES.adminMaterials} element={<AdminMaterials />} />
                        <Route path={ROUTES.adminSizes} element={<AdminSizes />} />
                        <Route path={ROUTES.adminPrices} element={<AdminPrices />} />
                        <Route path={ROUTES.adminOrders} element={<AdminOrders />} />
                        <Route path={ROUTES.adminShipping} element={<AdminShipping />} />
                    </Route>
                </Route>

                <Route path={ROUTES.main} element={<Main />}>
                    {/* Public routes */}
                    <Route path={ROUTES.home} element={<Home />} />

                    {/* Legal pages (public, within Main layout) */}
                    <Route path={ROUTES.privacyPolicy} element={<PrivacyPolicy />} />
                    <Route path={ROUTES.termsOfService} element={<TermsOfService />} />

                    {/* Protected routes - require authentication */}
                    <Route element={<ProtectedRoute />}>
                        <Route path={ROUTES.profile} element={<Profile />} />
                        <Route path={ROUTES.projects} element={<ProjectsList />} />
                        <Route path={ROUTES.newProject} element={<NewProject />} />
                        <Route path={ROUTES.orders} element={<OrdersList />} />
                        <Route path={ROUTES.orderDetail} element={<OrderDetail />} />
                        <Route path={ROUTES.cart} element={<Cart />} />
                        <Route path={ROUTES.checkout} element={<Checkout />} />
                    </Route>
                </Route>
                <Route path={ROUTES.notFound} element={<NotFound />} />
            </Routes>
        </div>
    )
}

export { App }
